import { useState, useEffect } from 'react';
import { User } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useProfileCompletion } from '@/hooks/use-profile-completion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { GhostClaimModal } from '@/components/GhostClaimModal';

interface Props {
  open: boolean;
  onDismiss: () => void;
}

export function ProfilePromptModal({ open, onDismiss }: Props) {
  const { user } = useAuth();
  const { clearOnComplete } = useProfileCompletion();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showGhostClaim, setShowGhostClaim] = useState(false);

  useEffect(() => {
    if (open) {
      setFirstName((user as any)?.firstName || '');
      setLastName((user as any)?.lastName || '');
      setShowGhostClaim(false);
    }
  }, [open, user]);

  const saveProfile = useMutation({
    mutationFn: async (data: { firstName: string; lastName: string }) => {
      const res = await fetch('/api/auth/user/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ firstName: data.firstName, lastName: data.lastName }),
      });
      if (!res.ok) throw new Error('Failed to save profile');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      clearOnComplete();
      setShowGhostClaim(true);
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to save profile. Try again.', variant: 'destructive' });
    },
  });

  function handleSave() {
    const name = firstName.trim();
    if (!name) {
      toast({ title: 'First name required', description: 'Please enter at least your first name.', variant: 'destructive' });
      return;
    }
    saveProfile.mutate({ firstName: name, lastName: lastName.trim() });
  }

  function handleFinalClose() {
    setShowGhostClaim(false);
    toast({ title: 'Profile saved!', description: 'Your player profile is ready.' });
    onDismiss();
  }

  if (!open) return null;

  if (showGhostClaim) {
    return (
      <GhostClaimModal
        open={true}
        onDismiss={handleFinalClose}
        onClaimed={handleFinalClose}
      />
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '16px',
      }}
      data-testid="profile-prompt-modal"
    >
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          backgroundColor: '#1e3a5f',
          borderRadius: '20px',
          padding: '28px',
          border: '2px solid #FACC15',
          boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
          position: 'relative',
        }}
      >
        <button
          onClick={onDismiss}
          style={{
            position: 'absolute',
            top: '12px',
            right: '16px',
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.6)',
            fontSize: '28px',
            fontWeight: 700,
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
          }}
          data-testid="button-pp-dismiss"
        >
          ×
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            backgroundColor: 'rgba(250,204,21,0.15)',
            border: '2px solid #FACC15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <User style={{ width: '22px', height: '22px', color: '#FACC15' }} />
          </div>
          <p style={{
            color: '#FACC15',
            fontWeight: 800,
            margin: 0,
            fontSize: '22px',
            letterSpacing: '0.3px',
            lineHeight: 1.2,
          }}>
            Set Up Your Profile
          </p>
        </div>

        <p style={{
          color: 'rgba(255,255,255,0.85)',
          fontWeight: 500,
          margin: '0 0 20px',
          fontSize: '15px',
          lineHeight: 1.5,
        }}>
          Your rounds, stats, and Razzy Ledger are waiting. Enter your name to track everything.
        </p>

        <div style={{
          backgroundColor: 'rgba(0,0,0,0.25)',
          borderRadius: '12px',
          padding: '18px',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={{
                display: 'block',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '12px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '6px',
              }}>
                First Name <span style={{ color: '#FACC15' }}>*</span>
              </label>
              <input
                id="pp-firstName"
                placeholder="First"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                data-testid="input-pp-first-name"
                style={{
                  width: '100%',
                  height: '44px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1.5px solid rgba(255,255,255,0.25)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: 600,
                  padding: '0 12px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#FACC15'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.25)'; }}
              />
            </div>
            <div>
              <label style={{
                display: 'block',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '12px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '6px',
              }}>
                Last Name
              </label>
              <input
                id="pp-lastName"
                placeholder="Last"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                data-testid="input-pp-last-name"
                style={{
                  width: '100%',
                  height: '44px',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1.5px solid rgba(255,255,255,0.25)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: 600,
                  padding: '0 12px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#FACC15'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.25)'; }}
              />
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saveProfile.isPending}
            data-testid="button-pp-save"
            style={{
              width: '100%',
              height: '52px',
              backgroundColor: saveProfile.isPending ? 'rgba(6,78,59,0.6)' : '#064E3B',
              border: '2px solid #FACC15',
              borderRadius: '12px',
              color: '#FACC15',
              fontSize: '17px',
              fontWeight: 800,
              cursor: saveProfile.isPending ? 'not-allowed' : 'pointer',
              letterSpacing: '0.3px',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => { if (!saveProfile.isPending) (e.target as HTMLButtonElement).style.backgroundColor = '#053f30'; }}
            onMouseLeave={(e) => { if (!saveProfile.isPending) (e.target as HTMLButtonElement).style.backgroundColor = '#064E3B'; }}
          >
            {saveProfile.isPending ? 'Saving…' : 'Set Up Profile'}
          </button>
        </div>
      </div>
    </div>
  );
}
